import * as React from 'react'

import { ChevronDownIcon, Cross1Icon } from '@radix-ui/react-icons'
import { PopoverPortal } from '@radix-ui/react-popover'
import { BaseHubAvatar, InvitedUserAvatar } from '~/components/basehub-avatar'
import { Button, ButtonLink } from '~/sotano-kit/primitives/button'
import { IconButton } from '~/sotano-kit/primitives/icon-button'
import {
  Popover,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
} from '~/sotano-kit/primitives/popover'
import { toast } from 'sonner'
import ph from 'posthog-js'
import * as va from '@vercel/analytics'
import { InviteForm } from '~/app/(dash)/_components/invite-form'
import { getId } from '@basehub/utils/id/index'
import { useTeamReplicache } from '~/hooks-data/use-team-replicache'
import { useSelectedTeamAndRepo } from '~/hooks-data/use-selected-team-and-repo'
import { clsx } from 'clsx'
import { InviteLinkDialog } from '../../(team)/settings/_components/invite'
import { getUserDataQueryKey, useUserData } from '~/hooks-data/use-user-data'
import { Repository } from '@basehub/data/replicache/index'
import { Skeleton } from '~/sotano-kit/primitives/skeleton'
import { PublicRepo } from '@basehub/data/replicache/mutators/_public-types'
import { ScrollArea } from '~/sotano-kit/primitives/scroll-area'
import { useTeamAndRepoMembers } from '~/hooks-data/use-team-and-repo-members'
import { PublicUser } from '~/app/api/users/public-user-schema'
import {
  isInviteExpired,
  isInviteGeneric,
} from '@basehub/data/replicache/mutators/team-space/invite-util'
import { useQueryClient } from '@tanstack/react-query'

const paddingInline = 16

export const ShareButton = () => {
  const data = useTeamReplicache().data
  const repo = useSelectedTeamAndRepo().data?.repo
  const usersAlreadyInvited = useTeamAndRepoMembers().data
  const [viewAllGuestsTriggered, setViewAllGuestsTriggered] =
    React.useState(false)
  const lockedInvites = (repo?.guests?.invites ?? [])
    .map((invite) => {
      if (isInviteGeneric(invite)) return null
      if (isInviteExpired(invite)) return null
      return { guest: invite, type: 'invite' as const }
    })
    .filter(Boolean)
  const guests = (repo?.guests?.list ?? []).map((guest) => ({
    guest,
    type: 'guest' as const,
  }))

  const invitesAndGuests = [...lockedInvites, ...guests]
  const firstInvitesAndGuests = invitesAndGuests.slice(
    0,
    viewAllGuestsTriggered ? invitesAndGuests.length : 6
  )
  const nonShownInvitesAndGuests =
    invitesAndGuests.length - firstInvitesAndGuests.length

  const queryClient = useQueryClient()

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button size="small">Share</Button>
      </PopoverTrigger>
      <PopoverPortal>
        <PopoverContent
          sideOffset={5}
          align="end"
          className="flex flex-col !p-0 w-[500px] min-h-min h-max max-h-[80vh] focus:outline-none radix-state-open:animate-popover-show radix-state-closed:invisible radix-state-closed:pointer-events-none"
          asChild
        >
          <ScrollArea className="flex flex-col" style={{ paddingInline }}>
            <PopoverHeader style={{ paddingInline }}>
              Share this repo
            </PopoverHeader>

            <InviteForm
              className="flex items-end mb-2 pt-4"
              label=""
              style={{ paddingInline }}
              onSubmit={async (e, resetForm) => {
                if (!repo) return
                e.preventDefault()

                const formData = new FormData(e.currentTarget)
                const emails = formData.get('emails')?.toString()
                const parsedEmails: string[] = []
                const teamMembers = usersAlreadyInvited?.teamMembers ?? []

                const repoGuestsPromise = (repo.guests?.list ?? []).map(
                  (invite) => {
                    const user = queryClient.getQueryData(
                      getUserDataQueryKey(invite.userId)
                    ) as PublicUser | undefined
                    if (user) return user
                  }
                )
                const repoGuests = (
                  await Promise.all(repoGuestsPromise)
                ).filter(Boolean)

                const alreadyInvitedOrMembersEmailMap = new Map<
                  string,
                  PublicUser | undefined
                >([
                  ...((repo.guests?.invites ?? []).map((invite) => [
                    invite.emailLock ?? '',
                    undefined,
                  ]) satisfies [string, undefined][]),
                  ...(teamMembers.map((member) => [
                    (member.email ?? '').toLowerCase(),
                    member,
                  ]) satisfies [string, PublicUser][]),
                  ...(repoGuests.map((member) => [
                    (member.email ?? '').toLowerCase(),
                    member,
                  ]) satisfies [string, PublicUser][]),
                ])

                ;(emails ?? '').split(',').forEach((email) => {
                  const trimmedEmail = email.trim().toLowerCase()
                  if (alreadyInvitedOrMembersEmailMap.has(trimmedEmail)) return
                  parsedEmails.push(trimmedEmail)
                })
                const dedupedEmails = Array.from(new Set(parsedEmails))

                if (!emails) return
                if (dedupedEmails.length === 0) {
                  toast.error('Emails already invited')
                  return
                }

                await data?.replicache.mutate.bulkInviteEmailsToRepo({
                  repoId: repo.id,
                  teamId: data.teamId,
                  emails: dedupedEmails,
                  invite: {
                    token: getId(),
                    createdAt: new Date().toISOString(),
                    userIdLock: null,
                    useCount: 0,
                  },
                })
                toast.success('Invited successfully')
                resetForm()

                va.track('Teammates invited', {
                  source: 'members settings',
                  emails,
                  teamId: data?.teamId ?? '',
                })
                ph.capture('Teammates invited', {
                  source: 'members settings',
                  emails,
                  teamId: data?.teamId,
                })
              }}
            />

            <InviteFooter style={{ paddingInline }} />

            <hr className="border-t border-dark-border-solid mt-4" />

            <div className="flex flex-col py-4" style={{ paddingInline }}>
              <p className="text-xs font-medium text-dark-muted mb-2">Guests</p>

              {firstInvitesAndGuests.length ? (
                <ul className="space-y-2">
                  {firstInvitesAndGuests.map((inviteOrGuest) => {
                    if (inviteOrGuest.type === 'guest') {
                      return (
                        <Guest
                          key={inviteOrGuest.guest.userId}
                          guest={inviteOrGuest.guest}
                        />
                      )
                    }

                    // is invite
                    return (
                      <Invite
                        key={
                          inviteOrGuest.guest.emailLock ??
                          inviteOrGuest.guest.userIdLock
                        }
                        repo={repo ?? undefined}
                        invite={inviteOrGuest.guest}
                      />
                    )
                  })}
                </ul>
              ) : (
                <p className="text-dark-faint font-medium text-xs">
                  No guests yet.
                </p>
              )}

              {Boolean(nonShownInvitesAndGuests) && (
                <Button
                  unstyled
                  focusStyled
                  onClick={() => {
                    setViewAllGuestsTriggered(true)
                  }}
                  className="text-dark-faint hover:text-dark-dim font-medium flex items-center text-xs cursor-default mt-2"
                >
                  <ChevronDownIcon className="inline align-middle mr-1" />
                  View all {nonShownInvitesAndGuests} invites and guests
                </Button>
              )}
            </div>
          </ScrollArea>
        </PopoverContent>
      </PopoverPortal>
    </Popover>
  )
}

const InviteFooter = (props: React.JSX.IntrinsicElements['div']) => {
  const [collapsed, setCollapsed] = React.useState(true)

  return (
    <div {...props}>
      <div className="flex justify-between text-xs">
        <Button
          unstyled
          focusStyled
          className={clsx(
            collapsed ? 'text-dark-faint' : 'text-dark-dim',
            'hover:text-dark-dim font-medium flex items-center cursor-default'
          )}
          onClick={() => setCollapsed((prevState) => !prevState)}
        >
          <ChevronDownIcon
            className={clsx(
              'inline align-middle mr-1',
              !collapsed && 'rotate-180'
            )}
          />
          What are guests?
        </Button>
        <InviteLinkDialog
          unstyled
          scope="repo"
          className="flex items-center gap-x-1 hover:text-dark-dim"
        />
      </div>

      {!collapsed && (
        <ul className="text-xs text-dark-muted space-y-1 pl-1.5 mt-1">
          <li className="list-item list-inside list-disc">
            They can read all your repo's content and leave comments
          </li>
          <li className="list-item list-inside list-disc">
            They can create new branches, for you to review and merge (or
            discard)
          </li>
          <li className="list-item list-inside list-disc">
            They cannot edit other user's branches nor the default branch
          </li>
          <li className="list-item list-inside list-disc">
            You can invite unlimited guests for free in all our plans
          </li>
        </ul>
      )}
    </div>
  )
}

const Invite = ({
  invite,
  repo,
}: {
  invite: NonNullable<Repository['guests']>['invites'][number]
  repo: Repository | PublicRepo | undefined
}) => {
  const { data: user } = useUserData(invite.userIdLock ?? undefined)
  const data = useTeamReplicache().data

  return (
    <li
      key={invite.userIdLock ?? invite.emailLock}
      className="flex items-center text-sm text-dark-dim overflow-hidden"
    >
      {user ? (
        <BaseHubAvatar
          id={user.id}
          name={user.username ?? ''}
          className="size-6 rounded-full shrink-0"
          forceSrc={user?.isMe ? (user.imageUrl ?? undefined) : undefined}
        />
      ) : (
        <InvitedUserAvatar size="small" invite={invite} className="shrink-0" />
      )}

      <span className="mx-2 flex overflow-hidden">
        <span className="overflow-hidden text-ellipsis">
          {user ? user.firstName + ' ' + user.lastName : invite.emailLock}
        </span>
        <span className="text-[12px] font-medium text-dark-muted bg-dark-shade ml-2 rounded-full px-1.5 py-0.5 whitespace-nowrap">
          Pending approval
        </span>
      </span>
      <IconButton
        className="text-dark-faint ml-auto"
        onClick={() => {
          if (!repo) return
          data?.replicache.mutate.removeRepoInvitation({
            repoId: repo.id,
            inviteToken: invite.token,
            emailLock: invite.emailLock,
          })
        }}
      >
        <Cross1Icon />
      </IconButton>
    </li>
  )
}

const Guest = ({
  guest,
}: {
  guest: NonNullable<Repository['guests']>['list'][number]
}) => {
  const { data: user } = useUserData(guest.userId)
  const teamAndRepo = useSelectedTeamAndRepo().data
  const repo = teamAndRepo?.repo
  const replicache = useTeamReplicache().data?.replicache
  const userData = useUserData(guest.userId)
  const queryClient = useQueryClient()

  if (!user) {
    return (
      <div className="flex items-center gap-x-2">
        <Skeleton height={24} className="rounded-full size-6" />
        <Skeleton height={20} className="flex-1" />
      </div>
    )
  }

  const completeNameOrUsername = user.firstName
    ? user?.firstName + ' ' + (user?.lastName ?? '')
    : (user.username ?? '')

  return (
    <li
      key={guest.userId}
      className="flex items-center text-sm text-dark-dim mb-2 last:mb-0"
      data-email={userData.data?.email}
    >
      <ButtonLink
        href={`/${user.username}`}
        unstyled
        focusStyled
        className="contents group hover:underline"
      >
        <BaseHubAvatar
          withBorder
          className="mr-2"
          id={user?.id}
          name={completeNameOrUsername}
        />{' '}
        <span className="group-hover:underline">{completeNameOrUsername}</span>
      </ButtonLink>
      <IconButton
        className="text-dark-faint ml-auto"
        onClick={async () => {
          if (!replicache || !repo) return

          await replicache.mutate.removeRepoGuest({
            repoId: repo.id,
            userId: guest.userId,
          })
          queryClient.invalidateQueries(getUserDataQueryKey(guest.userId))
        }}
      >
        Remove
      </IconButton>
    </li>
  )
}
